import * as React from 'react'

import { createContext } from '~logic/lib/react'

import { useCartContentState } from '../hooks/useCartContentState'

// the type that gets returned from the cart logic hidden in the separate custom hook
type TCartState = ReturnType<typeof useCartContentState>
// * there are 4 different context for improved performance – these context group values that are expected to change or be read together
//  - CartContent: the object that should represent up-to-date state of the order
//  - CartUpdate: the functions for modifying the order in some cart-related way (add/update/remove items)
//  - CartFetching: some of the cart updating GQL operation is running

type TCartFetchingContext = TCartState['isCartFetching']
type TCartContentContext = TCartState['cartContent']
type TCartUpdateContext = Pick<
  TCartState,
  'addProductToCart' | 'updateCartItems' | 'removeCartItems' | 'refetch'
>

const [ValueProvider, useCartContentCtx] = createContext<TCartContentContext>(`CartContent`)
const [UpdateProvider, useCartUpdateCtx] = createContext<TCartUpdateContext>(`CartUpdate`)
const [FetchingProvider, useCartFetchingCtx] = createContext<TCartFetchingContext>(`CartFetching`)

// type TQuery = typeof useQOrderForCart | typeof useQWaiterCart
// type TOptions<TUseQueryHook extends TQuery>
type TProps = RequiredChildren & FirstParam<typeof useCartContentState>

/**
 * Provider of cart related contexts.
 * Use `useCartContent` to access the order that the cart is rendering.
 * Use `useCartUpdate` to get the order updating functions (add/update/remove items).
 * Use `useCartFetching` to change something when some GQL operation that might update the cart is currently running.
 */
export const CartContentProvider: React.FC<TProps> = ({ children, ...loadOptions }) => {
  const {
    refetch,
    cartContent,
    addProductToCart,
    updateCartItems,
    removeCartItems,
    isCartFetching,
  } = useCartContentState(loadOptions)

  // keep the updateContext stale unless any of the callbacks has changed
  const updateContext = React.useMemo(
    () => ({
      refetch,
      addProductToCart,
      updateCartItems,
      removeCartItems,
    }),
    [addProductToCart, refetch, removeCartItems, updateCartItems],
  )

  return (
    <UpdateProvider value={updateContext}>
      <ValueProvider value={cartContent}>
        <FetchingProvider value={isCartFetching}>{children}</FetchingProvider>
      </ValueProvider>
    </UpdateProvider>
  )
}

export { useCartUpdateCtx, useCartContentCtx, useCartFetchingCtx }
